import {csvToArray} from '@netx/core-next/utils/array';
import {ATTRIBUTE_MULTIPLE_VALUE_TYPES} from '@netx/core-attribute/constants';
import {attributesStore} from '@netx/core-attribute/store/attributesStore';
import {SearchType} from '../rules/constants';
import {MetadataNoValueTypes} from '../rules/metadata/constants';
import type {EditablSearchRule, SearchRules} from '../rules/types';

const getNormalizedMultivalue = (value?: string | number) =>
	value == null || value === ''
		? []
		: value.toString().match(/^".*",.*"$/)
			? csvToArray(value.toString())
			: Array.isArray(value)
				? value
				: [value.toString()];

/**
 * Converts SearchRules (which are arrays of rule properties) to an array of SearchRule objects.
 * @param searchRules
 * @returns Array of SearchRule objects
 * @example
 * ```ts
 * const searchRules: SearchRules = {
 *   elementTypes: [1, 2],
 *   elementSubType1s: [0, 0],
 *   elementSubType2s: [0, 0],
 *   elementValue1s: ['value1', 'value2'],
 *   elementValue2s: ['value3', 'value4'],
 *   elementValue3s: ['value5', 'value6'],
 * };
 *
 * const ruleObjects = convertRulesToRuleObjects(searchRules);
 * console.log(ruleObjects);
 * // Output:
 * // [
 * //   {
 * //     elementType: 1,
 * //     elementSubType1: 0,
 * //     elementSubType2: 0,
 * //     elementValue1: 'value1',
 * //     elementValue2: 'value3',
 * //     elementValue3: 'value5',
 * //   },
 * //   {
 * //     elementType: 2,
 * //     elementSubType1: 0,
 * //     elementSubType2: 0,
 * //     elementValue1: 'value2',
 * //     elementValue2: 'value4',
 * //     elementValue3: 'value6',
 * //   },
 * // ]
 * ```
 */
export const convertRulesToRuleObjects = (searchRules: SearchRules) => {
	const ruleObjects: EditablSearchRule[] = [];

	// This is to support old (perhaps bookmarked or otherwise saved) advanced search urls
	const getMatchingAttributeValues = (index: number) => {
		const values: Array<string | number | string[] | number[]> = [];
		const elementType = searchRules.elementTypes[index];
		const elementSubType1 = searchRules.elementSubType1s[index];
		const elementSubType2 = searchRules.elementSubType2s[index];
		const elementValue2 = searchRules.elementValue2s[index];
		for (let i = index + 1, c = searchRules.elementTypes.length; i < c; i++) {
			if (
				searchRules.elementTypes[i] !== elementType ||
				searchRules.elementValue2s[i] !== elementValue2 ||
				// we are not looking at no value attribute rules
				(MetadataNoValueTypes as readonly number[]).includes(
					searchRules.elementSubType1s[i],
				) ||
				searchRules.elementSubType1s[i] !== elementSubType1 ||
				searchRules.elementSubType2s[i] !== elementSubType2
			)
				break;

			values.push(getNormalizedMultivalue(searchRules.elementValue1s[i]));
		}
		return values.flat();
	};

	for (let i = 0, c = searchRules.elementTypes.length; i < c; i++) {
		const elementType = searchRules.elementTypes[i];
		const elementSubType1 = searchRules.elementSubType1s[i];
		const elementSubType2 = searchRules.elementSubType2s[i];
		const elementValue1 = searchRules.elementValue1s[i];
		const elementValue2 = searchRules.elementValue2s[i];
		const elementValue3 = searchRules.elementValue3s[i];

		if (elementType === SearchType.METADATA) {
			const attr = attributesStore.getAttributeByName(elementValue2.toString());
			if (attr && ATTRIBUTE_MULTIPLE_VALUE_TYPES.includes(attr.type)) {
				const _elementValue1 = getNormalizedMultivalue(elementValue1);
				const matchingAttributeValues = getMatchingAttributeValues(i);
				ruleObjects.push({
					elementType,
					elementSubType1,
					elementSubType2,
					elementValue1: [..._elementValue1, ...matchingAttributeValues].flat(),
					elementValue2,
					elementValue3,
				});
				i = i + matchingAttributeValues.length;
				continue;
			}
		}

		ruleObjects.push({
			elementType,
			elementSubType1,
			elementSubType2,
			elementValue1,
			elementValue2,
			elementValue3,
		});
	}

	return ruleObjects;
};

export default convertRulesToRuleObjects;
